// Reduce model-emitted Markdown to plain text for ONE-LINE surfaces — the
// faded recap strip and the question headline — where rendering real Markdown
// is impossible (block elements would break the fixed-height / ellipsis
// layout) but raw `**`/`#`/backtick markers read as garbage. Prose surfaces
// render properly instead (MarkdownView); this is only for clipped text.
//
// The contract is "match what MarkdownView would show, as plain text":
// syntax that CommonMark treats as formatting is unwrapped; syntax it treats
// as literal text is left alone. Concretely: `**`/`*` emphasis is stripped
// even intraword (CommonMark bolds `a**b**c`), but `__`/`_` only when
// whitespace-delimited (CommonMark does NOT emphasize intraword underscores,
// so `mcp__loom__open_mr` and `dod_criteria` survive). Links collapse to
// their label only for URL-shaped destinations — `handlers[name](payload)`
// is code, not a link. Numbered-list markers are kept: "15. Juni 2026" at
// line start is prose, and "1. plan" reads fine in one line.
//
// No lookbehind anywhere: the Vite build target includes safari14, where a
// lookbehind regex throws SyntaxError at runtime (vitest on Node won't
// catch it).
export const stripMarkdownInline = (src: string): string =>
  src
    // Fenced-code markers (keep the body — it may be the whole message).
    // The marker's own line goes entirely, newline included, so no blank
    // line is left where the fence was.
    .replace(/^[ \t]*```[\w-]*[ \t]*\r?\n?/gm, "")
    // Line-leading block markers: headings, blockquotes, bullets.
    .replace(/^\s{0,3}(?:#{1,6}\s+|>\s?|[-*+]\s+)/gm, "")
    // Images / links → their label text. The destination must be URL-shaped
    // and may carry one level of balanced parens (Wikipedia-style
    // "…/Bern_(Stadt)" URLs).
    .replace(
      /!?\[([^\]]*)\]\((?:https?:\/\/|mailto:|\/|#)(?:[^()\s]|\([^()\s]*\))*\)/g,
      "$1",
    )
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/(^|\s)__([^\s_](?:[^_]*[^\s_])?)__(?=\s|$)/gm, "$1$2")
    .replace(/\*(\S(?:.*?\S)?)\*/g, "$1")
    .replace(/(^|\s)_([^_\n]+)_(?=\s|$)/gm, "$1$2")
    .replace(/~~(.+?)~~/g, "$1")
    .replace(/`([^`\n]+)`/g, "$1")
    .trim();
